// noinspection JSUnusedGlobalSymbols

import { subscribeToLoanContext } from "./utils/interactionIds";
import { initialize } from "./utils/googleAnalytics";
import { Subscription } from "rxjs";

/// <reference path="./utils/loan-context.ts" />
import { LoanContext, loanContext } from "./utils/loan-context";

/// <reference path="./utils/workitem-context.ts" />
import { WorkItemContext, workItemContext } from "./utils/workitem-context";

/// <reference path="./utils/query-context.ts" />
import { Query, queryContext } from "./utils/query-context";
import { getEnv } from "./utils/getEnv";

export interface UserDetails {
  fullName?: string;
  firstName?: string;
  uid?: string;
  entityName?: string;
}

export {
  isUserAuthenticated,
  fetchWithToken,
  getUserDetails,
  redirectToLogin,
  redirectToLogout,
  isUserValid,
} from "./components/Auth/IceSso/IceFunctions";

export { useGetWithToken } from "./utils/useGetWithToken";
export { hasPermission } from "./utils/hasPermission";
export { PlatformSecure } from "./components/PlatformSecure/PlatformSecure";

// noinspection JSUnusedGlobalSymbols
async function bootstrap(_props: any) {
  initialize();
  subscribeToLoanContext();
}

// noinspection JSUnusedGlobalSymbols
async function mount(_props: any) {
  /* mount function is required by single spa but is not used in this module */
}

// noinspection JSUnusedGlobalSymbols
async function unmount(_props: any) {
  /* unmount function is required by single spa but is not used in this module */
}

export { bootstrap, mount, unmount };

export { BackendFailureAlert } from "./components/BackendFailureAlert/BackendFailureAlert";

export interface BackendFailureAlertFields {
  message?: string;
  retryFunction?: () => void;
  reportable?: boolean;
  correlationId?: string;
  component?: string;
  componentStack?: string;
  exceptionInformation?: string;
  commandInformation?: string;
}

export type Context = {
  Type: string;
  Key: string;
};

interface ContextManager<T> {
  getContext(): T;

  setContext(t: T): Context[];

  unsetContext(): void;

  subscribe(handlers: ContextEventHandlers): Subscription;

  next(): void;

  isClosed(): boolean;
}

interface ContextManagerMap {
  inputText: ContextManager<Query>;
  loan: ContextManager<LoanContext>;
  workItem: ContextManager<WorkItemContext>;
}

enum ActionType {
  SET,
  UNSET,
}

interface Action {
  readonly doWhat: ActionType;
  readonly ctxKey: ContextKey;
  readonly context: LoanContext | WorkItemContext | Query | undefined;
}

interface CompleteContextState {
  inputText?: Query;
  loan?: LoanContext;
  workItem?: WorkItemContext;
}

const managerMap: ContextManagerMap = {
  inputText: queryContext,
  loan: loanContext,
  workItem: workItemContext,
};

export type ContextKey = keyof typeof managerMap;

export interface ContextEventHandlers {
  onSet: (context: LoanContext | WorkItemContext | Query) => void;
  onUnset: () => void;
}

export function subscribeToContextChange(
  key: ContextKey,
  handlers: ContextEventHandlers
): Subscription {
  return managerMap[key].subscribe(handlers);
}

export function setContext(key: ContextKey, value: LoanContext): void;
export function setContext(key: ContextKey, value: WorkItemContext): void;
export function setContext(key: ContextKey, value: Query): void;

export function setContext(key: ContextKey, value: unknown) {
  const actions = plan(key, value);

  execute(actions);
}

export function unsetContext(key: ContextKey) {
  const actions = plan(key, undefined);

  execute(actions);
}

function plan(key: ContextKey, value: unknown): Action[] {
  const requestedState = calculateRequestedState(key, value);
  const currentState = buildCurrentState();

  const actions: Action[] = [];

  if (key === "inputText") {
    actions.push({
      doWhat: value ? ActionType.SET : ActionType.UNSET,
      ctxKey: key,
      context: value as Query,
    });
  } else {
    for (const objKey in currentState) {
      if (objKey === "inputText") {
        continue;
      }

      // @ts-ignore
      const ctxValue = currentState[objKey];
      // @ts-ignore
      const requestedCtxValue = requestedState[objKey];

      const currentValueStr = ctxValue ? JSON.stringify(ctxValue) : "undefined";
      const requestedValueStr = requestedCtxValue ? JSON.stringify(requestedCtxValue) : "undefined";

      if (requestedValueStr !== currentValueStr) {
        if (requestedCtxValue) {
          actions.push({
            doWhat: ActionType.SET,
            ctxKey: objKey as ContextKey,
            context: requestedCtxValue,
          });
        } else {
          actions.push({
            doWhat: ActionType.UNSET,
            ctxKey: objKey as ContextKey,
            context: requestedCtxValue,
          });
        }
      }
    }
  }

  return actions;
}

function execute(actions: Action[]) {
  for (const act of actions) {
    const doWhat = act.doWhat;
    const ctxKey = act.ctxKey;
    const actionContext = act.context;

    if (doWhat === ActionType.SET) {
      switch (ctxKey) {
        case "inputText":
          managerMap[ctxKey].setContext(actionContext as Query);
          break;
        case "loan":
          managerMap[ctxKey].setContext(actionContext as LoanContext);
          break;
        case "workItem":
          managerMap[ctxKey].setContext(actionContext as WorkItemContext);
          break;
        default:
          throw new Error("We should never get here!");
      }
    } else if (doWhat === ActionType.UNSET) {
      switch (ctxKey) {
        case "inputText":
        case "loan":
        case "workItem":
          managerMap[ctxKey].unsetContext();
          break;
        default:
          throw new Error("We should never get here!");
      }
    } else {
      throw new Error("Action type not set");
    }

    publishContextChangeEvent(ctxKey);
  }
}

function calculateRequestedState(key: ContextKey, value: unknown): CompleteContextState {
  const requestedState: CompleteContextState = {
    inputText: undefined,
    loan: undefined,
    workItem: undefined,
  };

  // @ts-ignore
  requestedState[key] = value;

  if (value) {
    const hasAssociations = value.hasOwnProperty("Associations");

    if (hasAssociations) {
      // @ts-ignore
      const associations: Context[] = value["Associations"];
      // @ts-ignore
      for (const ctx: Context of associations) {
        try {
          // @ts-ignore
          requestedState[ctx.Type] = JSON.parse(ctx.Key);
        } catch (error) {
          console.error(`Failed to parse association: ${ctx.Key}`);
        }
      }
    } else {
      const currentWorkItem = getContext("workItem") as WorkItemContext;
      const associations = currentWorkItem?.Associations;

      if (associations) {
        const ctxString = JSON.stringify(value);

        let isLinked = false;
        for (const linkedCtx of associations) {
          const linkedCtxStr = linkedCtx.Key;

          if (linkedCtxStr === ctxString) {
            // @ts-ignore
            requestedState.workItem = currentWorkItem;
            isLinked = true;
          }
        }
      }
    }
  }

  return requestedState;
}

function buildCurrentState(): CompleteContextState {
  return {
    inputText: getContext("inputText") as Query,
    loan: getContext("loan") as LoanContext,
    workItem: getContext("workItem") as WorkItemContext,
  };
}

export function getContext(key: ContextKey) {
  return managerMap[key].getContext();
}

export function clearContext() {
  for (const [key, manager] of Object.entries(managerMap)) {
    manager.unsetContext();

    publishContextChangeEvent(key as ContextKey);
  }
}

function publishContextChangeEvent(key: ContextKey) {
  if (key !== "inputText" && !managerMap[key].isClosed()) {
    managerMap[key].next();
  }
}

export type { LoanContext } from "./utils/loan-context";
export type { WorkItemContext } from "./utils/workitem-context";
export type { Query } from "./utils/query-context";

export { CORE_BFF_BASE_URL } from "./utils/constants";

export { isFeatureEnabled } from "./utils/isFeatureEnabled";
export { isOneAdminFeatureEnabled } from "./utils/isOneAdminFeatureEnabled";
export { collectGAEvent } from "./utils/collectGAEvent";

export { useAlternateLayout } from "./utils/useAlternateLayout";
